import { Component, OnInit, computed, input, output, viewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatMiniFabButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { DateRangePickerComponent } from '../../date-range-picker/date-range-picker.component';
import { IDateRangePickerDate } from '../../date-range-picker/date-range-picker.interface';
import { MatDropdown } from '../../mat-dropdown/mat-dropdown.component';
import { TFilterCardFieldConfig } from '../filter-card.interface';
import { FilterCardService } from '../filter-card.service';

/**
 * Renders one field's value input and decides commit timing — reused by both `FilterCardAddComponent`
 * (adding a new filter) and `FilterCardChipComponent` (editing an existing one). A single-valued input
 * (button-toggle, date range, or a `singleSelection` dropdown) commits the instant a value lands; a
 * multi-select dropdown stays open, accumulating selections, until the caller clicks the submit button.
 *
 * `dateRange` also stays behind the submit button (`shouldSuppressAutoCommit`), but for a different
 * reason than multi-select: `DateRangePickerComponent`'s own back/forward arrows
 * (`configuration.showBackwardForward`) write into the same shared `FormControl` via `setValue`, so a
 * plain `control.valueChanges` subscription can't tell an arrow nudge apart from a real calendar pick —
 * both would auto-commit. `DateRangePickerComponent.datesChangedByCalendar` already makes that
 * distinction: it's only emitted from a completed calendar pick (or its own clear button), never from
 * the arrows. Wiring `onDatesChange` to that output instead commits a calendar pick immediately
 * (convenient, same instant feel as before) while an arrow nudge stays pending — editable further, or
 * confirmed with the submit button.
 *
 * `pendingChange` (mirroring digital-boards' `autoSubmit`) fires on *every* value change regardless of
 * `shouldSuppressAutoCommit` — including a still-open multi-select's individual checkbox clicks, or a
 * dateRange arrow nudge. Callers route it through the same channel as `commit`, just without collapsing
 * the open UI, so an in-progress edit "hidden-submits" behind the scenes: the caller's value updates
 * live (which is what makes the page-level Apply bar appear), while the field itself stays open until
 * an explicit commit (auto-commit, the submit button, or the page-level Apply button) closes it.
 */
@Component({
  imports: [
    DateRangePickerComponent,
    MatButtonToggle,
    MatButtonToggleGroup,
    MatDropdown,
    MatIcon,
    MatMiniFabButton,
    ReactiveFormsModule,
    TranslateModule,
  ],
  selector: 'app-filter-card-field-input',
  styleUrl: './filter-card-field-input.component.scss',
  templateUrl: './filter-card-field-input.component.html',
})
export class FilterCardFieldInputComponent<Name extends string> implements OnInit {
  public readonly field = input.required<TFilterCardFieldConfig<Name>>();
  public readonly initialValue = input<unknown>(null);
  public readonly commit = output<unknown>();
  public readonly pendingChange = output<unknown>();
  public readonly dismissed = output<void>();

  private readonly dropdown = viewChild(MatDropdown);

  public readonly control = new FormControl();
  public readonly shouldSuppressAutoCommit = computed(() => {
    const field = this.field();

    return field.type === 'dateRange' || (field.type === 'dropdown' && !field.configuration.singleSelection);
  });

  public ngOnInit(): void {
    this.control.setValue(this.initialValue() ?? null, { emitEvent: false });

    this.control.valueChanges.subscribe((value) => {
      this.pendingChange.emit(value);

      if (!this.shouldSuppressAutoCommit()) {
        this.commitIfHasValue(value);
      }
    });
  }

  public onDatesChange(value: IDateRangePickerDate | undefined): void {
    this.commitIfHasValue(value);
  }

  /** Opens a dropdown field's option list; no-op for other field types. */
  public openDropdown(): void {
    this.dropdown()?.open();
  }

  public submit(): void {
    this.commitIfHasValue(this.control.value);
  }

  private commitIfHasValue(value: unknown): void {
    if (FilterCardService.hasFilterCardValue(value)) {
      this.commit.emit(value);
    }
  }
}
